import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Modal,
  Row,
  Spin,
  Tag,
  message,
} from 'antd';
import {
  CheckCircleOutlined,
  CreditCardOutlined,
  CrownOutlined,
  RocketOutlined,
} from '@ant-design/icons';

import AdminHeader from '../../components/AdminHeader';
import { showErrorModal } from '../../components/Modal';
import { useSubscription } from '../../api/useSubscription';
import { Plan } from '../../models';
import {
  PENDING_CHECKOUT_STORAGE_KEY,
  formatLimitValue,
  formatPlanPrice,
  isPaidPlan,
} from '../../utils/planUtils';

const LIMIT_LABELS: { key: keyof Plan['limits']; label: string }[] = [
  { key: 'max_link_profiles', label: 'Review video pages' },
  { key: 'max_buttons', label: 'Buttons per page' },
  { key: 'max_links_per_page', label: 'Links per page' },
  { key: 'hide_vioo_logo', label: 'Remove Vioo logo' },
  { key: 'custom_button_colors', label: 'Custom button colors' },
  { key: 'analytics_enabled', label: 'Analytics & reporting' },
  { key: 'google_review_tracking', label: 'Google Review tracking' },
  { key: 'multi_user', label: 'Multi-user management' },
  { key: 'ai_insights', label: 'AI performance insights' },
];

function renderLimitValue(key: keyof Plan['limits'], value: unknown): React.ReactNode {
  if (typeof value === 'boolean') {
    return value ? (
      <Tag color="green">Included</Tag>
    ) : (
      <Tag>Not included</Tag>
    );
  }

  if (key.startsWith('max_')) {
    return formatLimitValue(value as number | null);
  }

  return String(value ?? '—');
}

function formatDate(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function getPlanActionLabel(plan: Plan, isCurrent: boolean): string {
  if (isCurrent) {
    return 'Current plan';
  }
  if (plan.coming_soon) {
    return 'Coming soon';
  }
  if (isPaidPlan(plan)) {
    return `Subscribe to ${plan.name}`;
  }
  return `Switch to ${plan.name}`;
}

type PlanColumnProps = {
  plan: Plan;
  isCurrent: boolean;
  isBusy: boolean;
  isSelected: boolean;
  onSelect: (plan: Plan) => void;
};

const PlanColumn: React.FC<PlanColumnProps> = ({
  plan,
  isCurrent,
  isBusy,
  isSelected,
  onSelect,
}) => {
  const priceDisplay = formatPlanPrice(plan);
  const paidPlan = isPaidPlan(plan);

  return (
    <Col xs={24} md={12} xl={8} className="flex">
      <Card
        className={`h-full w-full shadow-sm flex flex-col [&_.ant-card-body]:flex-1 [&_.ant-card-body]:flex [&_.ant-card-body]:flex-col ${
          plan.featured ? 'border-blue-500 border-2' : ''
        }`}
        title={
          <div className="flex items-center justify-between gap-2">
            <span>{plan.name}</span>
            {plan.badge && <Tag color="gold">{plan.badge}</Tag>}
          </div>
        }
        extra={
          isCurrent ? (
            <Tag color="green" icon={<CheckCircleOutlined />}>
              Current
            </Tag>
          ) : null
        }
      >
        <div className="flex flex-1 flex-col">
          <div className="flex-1">
            {plan.tagline && (
              <p className="text-sm text-gray-500 mb-2">{plan.tagline}</p>
            )}
            <p className="text-gray-700 mb-4 min-h-[72px]">{plan.description}</p>

            <div className="mb-4">
              <span className="text-3xl font-semibold text-gray-900">{priceDisplay}</span>
              {plan.price !== null && plan.price > 0 && (
                <span className="text-gray-500"> / {plan.period}</span>
              )}
            </div>

            <ul className="space-y-2 pl-0 list-none">
              {plan.features.map((feature) => (
                <li key={feature} className="flex items-start gap-2 text-sm text-gray-700">
                  <CheckCircleOutlined className="text-green-500 mt-0.5" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          <Button
            type={isPaidPlan(plan) ? 'primary' : 'default'}
            block
            size="large"
            className="mt-6 shrink-0"
            icon={
              !isCurrent
                ? paidPlan
                  ? <CreditCardOutlined />
                  : <RocketOutlined />
                : undefined
            }
            disabled={isCurrent || plan.coming_soon}
            loading={isBusy && isSelected}
            onClick={() => onSelect(plan)}
          >
            {getPlanActionLabel(plan, isCurrent)}
          </Button>
        </div>
      </Card>
    </Col>
  );
};

const SubscriptionPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    loading,
    changingPlan,
    creatingCheckout,
    plans,
    subscription,
    loadPlanManagement,
    changePlan,
    createCheckout,
    getCheckoutStatus,
    getSubscription,
  } = useSubscription();

  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [checkoutReturnLoading, setCheckoutReturnLoading] = useState(false);
  const [checkoutAlert, setCheckoutAlert] = useState<{
    type: 'success' | 'error' | 'info';
    message: string;
    description?: string;
  } | null>(null);

  const clearCheckoutQuery = useCallback(() => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('checkout');
    nextParams.delete('session_id');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const pollCheckoutCompletion = useCallback(
    async (sessionId: string) => {
      const maxAttempts = 12;
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        const session = await getCheckoutStatus(sessionId);
        if (session.status === 'completed') {
          await getSubscription();
          setCheckoutAlert({
            type: 'success',
            message: 'Payment successful',
            description: `Your ${session.plan.name} plan is now active.`,
          });
          sessionStorage.removeItem(PENDING_CHECKOUT_STORAGE_KEY);
          return;
        }
        if (session.status === 'failed' || session.status === 'expired') {
          setCheckoutAlert({
            type: 'error',
            message: 'Payment not completed',
            description:
              session.status === 'expired'
                ? 'The checkout session expired. Please try again.'
                : 'Your payment was declined or cancelled.',
          });
          sessionStorage.removeItem(PENDING_CHECKOUT_STORAGE_KEY);
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }

      setCheckoutAlert({
        type: 'info',
        message: 'Payment processing',
        description:
          'Your payment is still being confirmed. Refresh this page in a moment if your plan has not updated.',
      });
    },
    [getCheckoutStatus, getSubscription]
  );

  useEffect(() => {
    loadPlanManagement().catch(() => {
      showErrorModal('Failed to load plan information');
    });
  }, [loadPlanManagement]);

  useEffect(() => {
    const checkoutResult = searchParams.get('checkout');
    if (!checkoutResult) {
      return;
    }

    const sessionId =
      searchParams.get('session_id') ||
      sessionStorage.getItem(PENDING_CHECKOUT_STORAGE_KEY);

    clearCheckoutQuery();

    if (checkoutResult === 'failure') {
      sessionStorage.removeItem(PENDING_CHECKOUT_STORAGE_KEY);
      setCheckoutAlert({
        type: 'error',
        message: 'Payment cancelled',
        description: 'No charges were made. You can try again when ready.',
      });
      return;
    }

    if (checkoutResult === 'success') {
      if (!sessionId) {
        getSubscription()
          .then(() => {
            setCheckoutAlert({
              type: 'success',
              message: 'Welcome back',
              description: 'If your plan has not updated yet, please refresh in a moment.',
            });
          })
          .catch(() => {
            setCheckoutAlert({
              type: 'info',
              message: 'Payment submitted',
              description: 'We are confirming your payment. Please refresh shortly.',
            });
          });
        return;
      }

      setCheckoutReturnLoading(true);
      pollCheckoutCompletion(sessionId)
        .catch(() => {
          setCheckoutAlert({
            type: 'error',
            message: 'Unable to confirm payment',
            description: 'Please refresh the page or contact support if your plan did not update.',
          });
        })
        .finally(() => {
          setCheckoutReturnLoading(false);
        });
    }
  }, [searchParams, clearCheckoutQuery, getSubscription, pollCheckoutCompletion]);

  const currentPlan = subscription?.plan;
  const currentPlanId = currentPlan?.id;
  const periodEnd = formatDate(subscription?.current_period_end);

  const sortedPlans = useMemo(
    // () => [...plans].sort((a, b) => (a.featured === b.featured ? 0 : a.featured ? -1 : 1)),
    () => plans,
    [plans]
  );

  const isBusy = changingPlan || creatingCheckout;

  const openChangePlan = (plan: Plan) => {
    if (plan.id === currentPlanId) {
      return;
    }
    setSelectedPlan(plan);
    setConfirmVisible(true);
  };

  const handleConfirmChange = () => {
    if (!selectedPlan) {
      return;
    }

    if (isPaidPlan(selectedPlan)) {
      createCheckout(selectedPlan.slug)
        .then((session) => {
          sessionStorage.setItem(
            PENDING_CHECKOUT_STORAGE_KEY,
            session.clover_checkout_session_id
          );
          window.location.href = session.checkout_url;
        })
        .catch((error) => {
          const apiMessage = error?.response?.data?.msg;
          showErrorModal(apiMessage || 'Failed to start checkout');
        });
      return;
    }

    changePlan(selectedPlan.slug)
      .then(() => {
        message.success(`Your plan has been changed to ${selectedPlan.name}.`);
        setConfirmVisible(false);
        setSelectedPlan(null);
      })
      .catch((error) => {
        const apiMessage = error?.response?.data?.msg;
        showErrorModal(apiMessage || 'Failed to change plan');
      });
  };

  const selectedPlanIsPaid = selectedPlan ? isPaidPlan(selectedPlan) : false;

  return (
    <AdminHeader>
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Plan & Subscription</h1>
              <p className="mt-1 text-sm text-gray-500">
                Manage your subscription and upgrade through secure Clover checkout.
              </p>
            </div>
          </div>

          {checkoutReturnLoading && (
            <div className="flex justify-center py-8">
              <Spin tip="Confirming your payment..." size="large" />
            </div>
          )}

          {checkoutAlert && !checkoutReturnLoading && (
            <Alert
              type={checkoutAlert.type}
              showIcon
              closable
              className="mb-6"
              message={checkoutAlert.message}
              description={checkoutAlert.description}
              onClose={() => setCheckoutAlert(null)}
            />
          )}

          {loading && !checkoutReturnLoading ? (
            <div className="flex justify-center py-16">
              <Spin size="large" />
            </div>
          ) : (
            <>
              {currentPlan && (
                <Card
                  title={
                    <span className="inline-flex items-center gap-2">
                      <CrownOutlined />
                      Current Plan
                    </span>
                  }
                  className="mb-8 shadow-sm"
                >
                  <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <h2 className="text-xl font-semibold text-gray-900 m-0">
                          {currentPlan.name}
                        </h2>
                        {currentPlan.badge && <Badge count={currentPlan.badge} />}
                        <Tag color="blue">{subscription?.status ?? 'active'}</Tag>
                      </div>
                      {currentPlan.tagline && (
                        <p className="text-gray-500 mb-2">{currentPlan.tagline}</p>
                      )}
                      <p className="text-gray-700 max-w-2xl">{currentPlan.description}</p>
                      <p className="mt-4 text-2xl font-semibold text-gray-900">
                        {formatPlanPrice(currentPlan)}
                        {currentPlan.price !== null && currentPlan.price > 0 && (
                          <span className="text-base font-normal text-gray-500">
                            {' '}
                            / {currentPlan.period}
                          </span>
                        )}
                      </p>
                      {periodEnd && isPaidPlan(currentPlan) && (
                        <p className="mt-2 text-sm text-gray-500">
                          Current billing period ends {periodEnd}
                        </p>
                      )}
                    </div>

                    <Descriptions
                      column={1}
                      size="small"
                      bordered
                      className="min-w-[280px]"
                    >
                      {LIMIT_LABELS.map(({ key, label }) => (
                        <Descriptions.Item key={key} label={label}>
                          {renderLimitValue(key, currentPlan.limits?.[key])}
                        </Descriptions.Item>
                      ))}
                    </Descriptions>
                  </div>
                </Card>
              )}

              <div className="mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Available Plans</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Paid plans open Clover secure checkout. Free downgrades apply immediately.
                </p>
              </div>

              <Row gutter={[24, 24]} className="items-stretch">
                {sortedPlans.map((plan) => (
                  <PlanColumn
                    key={plan.id}
                    plan={plan}
                    isCurrent={plan.id === currentPlanId}
                    isBusy={isBusy}
                    isSelected={selectedPlan?.id === plan.id}
                    onSelect={openChangePlan}
                  />
                ))}
              </Row>
            </>
          )}
        </div>
      </div>

      <Modal
        title={selectedPlanIsPaid ? 'Confirm subscription' : 'Confirm plan change'}
        open={confirmVisible}
        onOk={handleConfirmChange}
        onCancel={() => {
          if (isBusy) {
            return;
          }
          setConfirmVisible(false);
          setSelectedPlan(null);
        }}
        okText={
          selectedPlanIsPaid
            ? `Pay with Clover`
            : `Switch to ${selectedPlan?.name ?? 'plan'}`
        }
        confirmLoading={isBusy}
        okButtonProps={{
          icon: selectedPlanIsPaid ? <CreditCardOutlined /> : undefined,
        }}
      >
        {selectedPlan && (
          <div className="space-y-3">
            <p>
              You are about to {selectedPlanIsPaid ? 'subscribe to' : 'switch to'}{' '}
              <strong>{selectedPlan.name}</strong>
              {!selectedPlanIsPaid && currentPlan && (
                <>
                  {' '}
                  from <strong>{currentPlan.name}</strong>
                </>
              )}
              .
            </p>
            <p className="text-gray-600">
              Price: {formatPlanPrice(selectedPlan)}
              {selectedPlan.price !== null && selectedPlan.price > 0
                ? ` / ${selectedPlan.period}`
                : ''}
            </p>
            {selectedPlanIsPaid ? (
              <p className="text-sm text-gray-500">
                You will be redirected to Clover secure checkout to complete payment.
                Your plan activates once payment is confirmed.
              </p>
            ) : (
              <p className="text-sm text-gray-500">
                Plan limits and features will update immediately across your admin tools.
              </p>
            )}
          </div>
        )}
      </Modal>
    </AdminHeader>
  );
};

export default SubscriptionPage;
